/**
 * Sidebar component for notes page with filters and navigation
 */

import React from 'react';
import { XMarkIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { NoteType } from '../../types/reading-notes';

interface ChapterInfo {
  number: number;
  name: string;
  noteCount: number;
}

interface NotesSidebarProps {
  selectedTypes: Set<NoteType>;
  onToggleType: (type: NoteType) => void;
  chapters: ChapterInfo[];
  isOpen: boolean;
  onClose: () => void;
  activeChapter: number;
  onChapterSelect: (chapterNumber: number) => void;
  onBackClick: () => void;
  generalNotesCount: number;
}

export default function NotesSidebar({
  selectedTypes,
  onToggleType,
  chapters,
  isOpen,
  onClose,
  activeChapter,
  onChapterSelect,
  onBackClick,
  generalNotesCount
}: NotesSidebarProps) {
  const noteTypeFilters: Array<{ type: NoteType; label: string }> = [
    { type: 'highlight', label: 'Highlights' },
    { type: 'thought', label: 'Thoughts' },
    { type: 'chat_answer', label: 'AI Answers' }
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 lg:top-[52px] left-0 h-screen lg:h-[calc(100vh-52px)] w-64 flex flex-col bg-gray-50 dark:bg-slate-900/80 border-r border-gray-200 dark:border-slate-700/50 z-50 lg:z-40 transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Sidebar Header - Back to Library (desktop only) */}
        <div className="hidden lg:flex flex-shrink-0 h-[73px] px-6 items-center border-b border-gray-200 dark:border-slate-700/50">
          <button
            onClick={onBackClick}
            className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-slate-400 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>Library</span>
          </button>
        </div>

        {/* Fixed Top Section - Mobile Close & Filters */}
        <div className="flex-shrink-0 px-6 pt-6 pb-4 space-y-6 lg:pt-6">
          {/* Mobile Close Button */}
          <div className="lg:hidden flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-slate-400">Filters</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-slate-400" />
            </button>
          </div>

          {/* Note Type Filters */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-slate-400 mb-2">
              Filter by Type
            </label>
            <div className="space-y-1">
              {noteTypeFilters.map(({ type, label }) => {
                return (
                  <label
                    key={type}
                    className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTypes.has(type)}
                      onChange={() => onToggleType(type)}
                      className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-0"
                    />
                    <span className="text-sm text-gray-700 dark:text-slate-300">
                      {label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Scrollable Navigation */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
          {/* General Notes - above chapters */}
          <div className="mb-4">
            <button
              onClick={() => {
                onChapterSelect(0); // 0 = general notes
                onClose();
              }}
              className={`w-full text-left px-3 py-2 rounded-xl transition-colors ${
                activeChapter === 0
                  ? 'text-white'
                  : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800/50 hover:text-cyan-600 dark:hover:text-cyan-400'
              }`}
              style={activeChapter === 0 ? {
                background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
              } : {}}
            >
              General Notes ({generalNotesCount})
            </button>
          </div>

          {/* Chapter Navigation */}
          {chapters.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-slate-400 mb-2">
                Chapters
              </h3>
              <div className="space-y-0.5">
                {chapters.map((chapter) => {
                  const isActive = chapter.number === activeChapter;
                  return (
                    <button
                      key={chapter.number}
                      onClick={() => {
                        onChapterSelect(chapter.number);
                        onClose();
                      }}
                      className={`w-full text-left block px-3 py-2 rounded-xl transition-colors truncate ${
                        isActive
                          ? 'text-white'
                          : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800/50 hover:text-cyan-600 dark:hover:text-cyan-400'
                      }`}
                      style={isActive ? {
                        background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                      } : {}}
                      title={chapter.name}
                    >
                      {chapter.number === 0 ? 'Front Matter' : `${chapter.number}. ${chapter.name}`}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
